import { ApiErrorBody } from "@qlik-scheduler/shared";

// Thrown for any non-2xx response that ISN'T a 401 — 401s redirect instead.
// The wrapper parses the API's standard ApiErrorBody envelope so callers get
// the structured { code, message, details } even when the network said "no".
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type ApiRequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

// Per Phase 1 decision #3: the JWT cookie has a 15-min cliff. On 401, we do a
// top-frame navigation (NOT a fetch) to the SSO start endpoint with the
// current path+search as `return_to`, so the portal bounces us back. Using
// window.location.href is deliberate — fetch-following-a-redirect would burn
// the bounce inside an XHR.
function redirectToSso(): never {
  const returnTo = window.location.pathname + window.location.search;
  window.location.href =
    "/api/portal-sso/start?return_to=" + encodeURIComponent(returnTo);
  // Throw an error to halt the calling code path. The page is about to nav away;
  // any pending promises shouldn't resolve normally.
  throw new ApiError(401, "UNAUTHENTICATED", "Session expired; redirecting.");
}

async function parseErrorBody(
  res: Response,
): Promise<{ code: string; message: string; details?: Record<string, unknown> }> {
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return {
      code: "UNKNOWN",
      message: `HTTP ${res.status} ${res.statusText}`.trim(),
    };
  }
  const parsed = ApiErrorBody.safeParse(raw);
  if (parsed.success) {
    return parsed.data.error;
  }
  return {
    code: "UNKNOWN",
    message: `HTTP ${res.status} ${res.statusText}`.trim(),
  };
}

export async function apiRequest<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<T> {
  const { method = "GET", body, signal, headers } = opts;
  const init: RequestInit = {
    method,
    credentials: "same-origin", // session cookie rides along
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    signal,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path, init);

  if (res.status === 401) {
    redirectToSso(); // throws
  }

  if (!res.ok) {
    const err = await parseErrorBody(res);
    throw new ApiError(res.status, err.code, err.message, err.details);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  // Assume JSON for 2xx; routes that return empty bodies should use 204.
  return (await res.json()) as T;
}
